import { getMedusaBackendUrl } from "@lib/config";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReviewSortOption = "newest" | "highest" | "lowest" | "most_helpful";

export interface ReviewMedia {
  url: string;
  type: "image" | "video";
}

export interface Review {
  id: string;
  title?: string;
  content: string;
  rating: number;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  verified_purchase?: boolean;
  verified_source?: "none" | "order_history" | "review_token";
  created_at: string;
  media_urls?: string[];
  merchant_reply?: string;
  helpful_count?: number;
  unhelpful_count?: number;
  pros?: string;
  cons?: string;
  recommend?: boolean;
}

export interface ReviewsResponse {
  reviews: Review[];
  count: number;
  limit: number;
  offset: number;
  average_rating: number;
  rating_distribution: Record<"1" | "2" | "3" | "4" | "5", number>;
  recommendation_rate: number;
}

export interface FetchReviewsParams {
  limit?: number;
  offset?: number;
  sort?: ReviewSortOption;
  rating?: number;
  with_media?: boolean;
}

export interface CreateReviewPayload {
  product_id: string;
  content: string;
  rating: number;
  first_name: string;
  last_name: string;
  title?: string;
  pros?: string;
  cons?: string;
  recommend?: boolean;
  media_urls?: string[];
  verified_purchase?: boolean;
  verified_source?: "none" | "order_history" | "review_token";
}

// ─── Map Puck sort values to API sort values ──────────────────────────────────

export function toApiSort(sort: string | undefined): ReviewSortOption {
  switch (sort) {
    case "helpful": return "most_helpful";
    case "rating_high": return "highest";
    case "rating_low": return "lowest";
    default: return "newest";
  }
}

// ─── API calls ────────────────────────────────────────────────────────────────

export async function fetchProductReviews(
  productId: string,
  params: FetchReviewsParams = {}
): Promise<ReviewsResponse> {
  const baseUrl = getMedusaBackendUrl();
  const qs = new URLSearchParams();

  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  if (params.sort) qs.set("sort", params.sort);
  if (params.rating != null) qs.set("rating", String(params.rating));
  if (params.with_media) qs.set("with_media", "true");

  const res = await fetch(
    `${baseUrl}/store/products/${productId}/reviews?${qs.toString()}`,
    { next: { revalidate: 0 } } // always fresh on client; use cache: 'no-store' on server if needed
  );

  if (!res.ok) throw new Error(`Failed to fetch reviews: ${res.status}`);
  return res.json();
}

export async function createReview(
  payload: CreateReviewPayload,
): Promise<Review> {
  const baseUrl = getMedusaBackendUrl();
  const res = await fetch(`${baseUrl}/store/reviews`, {
    method: "POST",
    credentials: "include", // pass Medusa JWT session cookie
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error((error as any)?.message ?? `Failed to submit review: ${res.status}`);
  }
  return res.json();
}

export async function voteReview(
  reviewId: string,
  isHelpful: boolean,
): Promise<void> {
  const baseUrl = getMedusaBackendUrl();
  const res = await fetch(`${baseUrl}/store/reviews/${reviewId}/vote`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_helpful: isHelpful }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error((error as any)?.message ?? `Vote failed: ${res.status}`);
  }
}

export async function uploadReviewMedia(  reviewId: string,
  file: File,
): Promise<{ url: string; type: "image" | "video" }> {
  const baseUrl = getMedusaBackendUrl();
  const form = new FormData();
  form.append("file", file);

  const res = await fetch(`${baseUrl}/store/reviews/${reviewId}/media`, {
    method: "POST",
    credentials: "include",
    body: form,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error((error as any)?.message ?? `Upload failed: ${res.status}`);
  }
  const { media } = await res.json();
  return media;
}
